// Sdílené typy a číselníky pro frontend, backend a bránu.
import type { DatasetSnapshot, WidgetSpec } from './widgets';

export * from './widgets';
export * from './reldates';
export * from './widget-guide';
export * from './transcripts';
export * from './schedule';
export * from './memory';

export type Role = 'vedeni' | 'pm' | 'tym';

export const ROLE_LABEL: Record<Role, string> = {
  vedeni: 'Vedení',
  pm: 'Projektový manažer',
  tym: 'Realizační tým',
};

export type Theme = 'auto' | 'light' | 'dark';

export type MemoryType = 'profil' | 'cesta' | 'preference' | 'poznamka';

export const MEMORY_TYPE_LABEL: Record<MemoryType, string> = {
  profil: 'Profil',
  cesta: 'Cesta k datům',
  preference: 'Preference',
  poznamka: 'Poznámka',
};

/* ---------- nástroje brány a jejich lidské popisy ---------- */

export interface ToolLabel {
  /** Text během běhu kroku („Načítám výkazy z Prdele…“). */
  running: string;
  /** Text po dokončení kroku. */
  done: string;
  /** Zdroj dat, jak se ukáže v detailu kroku. */
  source: string;
}

export const TOOL_LABELS: Record<string, ToolLabel> = {
  whoami: { running: 'Ověřuji, kdo se ptá', done: 'Ověřen uživatel', source: 'Aplikace: profil' },
  vykazy_moje: { running: 'Načítám výkazy z Vykazovníku', done: 'Výkazy z Vykazovníku', source: 'Prdel: výkazy' },
  vykazy_vcera: { running: 'Načítám včerejší výkazy', done: 'Včerejší výkazy', source: 'Prdel: výkazy (standup)' },
  muj_prehled: { running: 'Načítám přehled týdne', done: 'Přehled týdne', source: 'Prdel: přehled' },
  ukoly_moje: { running: 'Načítám úkoly z Projektovníku', done: 'Úkoly z Projektovníku', source: 'Prdel: úkoly' },
  ukol_detail: { running: 'Načítám detail úkolu', done: 'Detail úkolu', source: 'Prdel: úkoly' },
  ukoly_souhrn: { running: 'Počítám úkoly', done: 'Souhrn úkolů', source: 'Prdel: úkoly' },
  projekty_moje: { running: 'Načítám projekty', done: 'Projekty z Projektovníku', source: 'Prdel: projekty' },
  projekt_detail: { running: 'Načítám detail projektu', done: 'Detail projektu', source: 'Prdel: projekty' },
  vytizeni: { running: 'Počítám vytížení', done: 'Vytížení', source: 'Prdel: vytížení' },
  vykazy_souhrn: { running: 'Sčítám výkazy', done: 'Součty výkazů', source: 'Prdel: výkazy' },
  vykazy_pokryti: { running: 'Hledám nevykázané dny', done: 'Pokrytí výkazů', source: 'Prdel: výkazy' },
  odhad_vs_skutecnost: { running: 'Porovnávám odhady s výkazy', done: 'Odhad proti skutečnosti', source: 'Prdel: úkoly a výkazy' },
  volno: { running: 'Načítám dovolené', done: 'Dovolené a absence', source: 'Prdel: Směny' },
  kolegove: { running: 'Hledám v adresáři', done: 'Adresář kolegů', source: 'Prdel: lidé' },
  op_nazvy: { running: 'Dohledávám obchodní případy', done: 'Obchodní případy', source: 'Prdel: Raynet' },
  klient_hledat: { running: 'Hledám klienta', done: 'Hledání klienta', source: 'Prdel: Raynet' },
  klient_detail: { running: 'Načítám klienta', done: 'Detail klienta', source: 'Prdel: Raynet' },
  prepis: { running: 'Načítám přepis schůzky', done: 'Přepis schůzky', source: 'Přepisy schůzek' },
  prepisy_klienti: { running: 'Načítám složky přepisů', done: 'Složky přepisů', source: 'Přepisy schůzek' },
  prepisy_klienta: { running: 'Načítám schůzky klienta', done: 'Schůzky klienta', source: 'Přepisy schůzek' },
  procesy_hledat: { running: 'Hledám ve firemních procesech', done: 'Hledání v procesech', source: 'Toolbox: procesy' },
  procesy_seznam: { running: 'Načítám seznam procesů', done: 'Seznam procesů', source: 'Toolbox: procesy' },
  proces_kroky: { running: 'Načítám kroky procesu', done: 'Kroky procesu', source: 'Toolbox: procesy' },
  proces_krok: { running: 'Načítám detail kroku', done: 'Detail kroku procesu', source: 'Toolbox: procesy' },
  proces_dalsi: { running: 'Zjišťuji další kroky', done: 'Další kroky procesu', source: 'Toolbox: procesy' },
  procesy_legenda: { running: 'Načítám legendu procesů', done: 'Legenda procesů', source: 'Toolbox: procesy' },
  wiki_hledat: { running: 'Hledám ve wiki', done: 'Hledání ve wiki', source: 'SMwiki' },
  kontakt_kolega: { running: 'Hledám kontakt', done: 'Kontakt na kolegu', source: 'Microsoft 365: lidé' },
  kalendar_muj: { running: 'Načítám kalendář', done: 'Můj kalendář', source: 'Microsoft 365: kalendář' },
  posta_hledat: { running: 'Hledám v poště', done: 'Hledání v poště', source: 'Microsoft 365: pošta' },
  posta_zprava: { running: 'Čtu e-mail', done: 'E-mail', source: 'Microsoft 365: pošta' },
  soubory_hledat: { running: 'Hledám v SharePointu a OneDrivu', done: 'Hledání v souborech', source: 'Microsoft 365: soubory' },
  zasedacky: { running: 'Zjišťuji obsazenost zasedaček', done: 'Obsazenost zasedaček', source: 'Microsoft 365: místnosti' },
  dostupnost_lidi: { running: 'Zjišťuji dostupnost kolegů', done: 'Dostupnost kolegů', source: 'Microsoft 365: kalendáře' },
  tv_obsah: { running: 'Načítám obsah lobby TV', done: 'Obsah lobby TV', source: 'Toolbox: TV' },
  zapasy: { running: 'Načítám zápasy', done: 'Zápasy', source: 'Toolbox: zápasy' },
  jidelni_listek: { running: 'Načítám jídelní lístek', done: 'Jídelní lístek', source: 'Bezva Jídlo' },
  naplanovat_ulohu: { running: 'Plánuji úlohu', done: 'Naplánovaná úloha', source: 'Aplikace: úlohy' },
  naplanovane_ulohy: { running: 'Načítám naplánované úlohy', done: 'Naplánované úlohy', source: 'Aplikace: úlohy' },
  zrusit_ulohu: { running: 'Ruším úlohu', done: 'Zrušená úloha', source: 'Aplikace: úlohy' },
  pamet_cist: { running: 'Čtu paměť', done: 'Přečtena paměť', source: 'Aplikace: paměť' },
  pamet_zapsat: { running: 'Zapisuji do paměti', done: 'Zapsáno do paměti', source: 'Aplikace: paměť' },
  finance_prehled: { running: 'Načítám finance z Pohody', done: 'Finance z Pohody', source: 'Pohoda: finance' },
};

/** `brana__vykazy_moje` → `vykazy_moje`. */
export function toolBaseName(name: string): string {
  return name.replace(/^brana__/, '');
}

export function toolLabel(name: string): ToolLabel {
  const base = toolBaseName(name);
  return TOOL_LABELS[base] ?? { running: `Volám nástroj ${base}`, done: `Nástroj ${base}`, source: base };
}

/** Zdroj z odpovědi brány (`mock:/sam/vykazovnik`) → čitelný název. */
export function sourceLabel(source: string): string {
  const path = source.replace(/^(mock|prdel|toolbox|graph|app|procesy|smwiki|bezvajidlo|prepisy):/, '');
  const map: Record<string, string> = {
    '/sam/vykazovnik': 'Vykazovník',
    '/sam/vykazovnik/standup': 'Vykazovník (včera)',
    '/sam-chat/reports': 'Vykazovník',
    '/sam-chat/reports/previous-day': 'Vykazovník (včera)',
    '/sam-chat/reports/summary': 'Vykazovník (součty)',
    '/sam-chat/reports/coverage': 'Vykazovník a Směny',
    '/sam-chat/me/overview': 'Vykazovník a Projektovník',
    '/sam-chat/me/estimate-vs-actual': 'Projektovník a Vykazovník',
    '/sam-chat/tasks': 'Projektovník: úkoly',
    '/sam-chat/tasks/{id}': 'Projektovník: úkol',
    '/sam-chat/tasks/summary': 'Projektovník: úkoly',
    '/sam-chat/projects': 'Projektovník: projekty',
    '/sam-chat/projects/{id}': 'Projektovník: projekt',
    '/sam-chat/workload': 'Projektovník: vytížení',
    '/sam-chat/time-off': 'Směny',
    '/sam-chat/people': 'Prdel: lidé',
    '/sam-chat/ops/lookup': 'Raynet',
    '/sam-chat/clients/search': 'Raynet: klienti',
    '/sam-chat/clients/{id}': 'Raynet: klient',
    '/anal/pohoda': 'Pohoda přes Prdel',
    '/api/procesy': 'Toolbox: procesy',
    '/api/procesy/hledat': 'Toolbox: procesy',
    '/api/procesy/kroky': 'Toolbox: procesy',
    '/api/procesy/krok': 'Toolbox: procesy',
    '/api/procesy/dalsi': 'Toolbox: procesy',
    '/api/procesy/legenda': 'Toolbox: procesy',
    '/api/sections': 'SMwiki',
    '/users': 'Microsoft 365: lidé',
    '/calendarView': 'Microsoft 365: kalendář',
    '/places/rooms': 'Microsoft 365: místnosti',
    '/me/messages': 'Microsoft 365: pošta',
    '/me/messages/{id}': 'Microsoft 365: pošta',
    '/search/query': 'Microsoft 365: SharePoint a OneDrive',
    '/calendar/getSchedule': 'Microsoft 365: kalendáře',
    '/sam/television/sources': 'Toolbox: lobby TV',
    '/sam/matches': 'Toolbox: zápasy',
    '/jidelni-listek': 'Bezva Jídlo',
    '/transcripts': 'Přepisy schůzek',
    '/clients': 'Přepisy schůzek',
    '/clients/{slug}/meetings': 'Přepisy schůzek',
    memory: 'Aplikace: paměť',
    schedules: 'Aplikace: naplánované úlohy',
    users: 'Aplikace: profil',
  };
  return map[path] ?? path;
}

/* ---------- kroky tahu ---------- */

export type StepStatus = 'running' | 'done' | 'denied' | 'error';

export interface Step {
  id: string;
  /** Jméno nástroje bez prefixu serveru (`vykazy_moje`). */
  tool: string;
  status: StepStatus;
  startedAt: string;
  endedAt?: string;
  /** Detail z auditu brány, doplní se po dokončení tahu. */
  detail?: StepDetail;
}

export interface StepDetail {
  source?: string;
  params?: Record<string, unknown>;
  rows?: number | null;
  decision: 'allow' | 'deny';
  rule?: string | null;
  reason?: string | null;
  ms?: number | null;
  ts: string;
}

export interface SourceRef {
  source: string;
  label: string;
  fetchedAt: string;
  /** Konkrétní záznam ve zdroji, u přepisu začátek schůzky `YYYY-MM-DDTHH:MM` (odkazy do přepisu se ověřují proti němu). */
  ref?: string;
}

/** Přepisy, které agent v tahu opravdu načetl přes bránu: jen na ně smí vést klikací odkaz. */
export function verifiedTranscripts(sources: SourceRef[] | undefined): Set<string> {
  return new Set((sources ?? []).filter((s) => s.ref && /(^|:)\/transcripts$/.test(s.source)).map((s) => s.ref!));
}

/* ---------- API DTO ---------- */

export interface UserDTO {
  id: string;
  email: string;
  name: string;
  role: Role;
  /** Správce appky (uživatelé, role, přístupy všech). Nezávislé na roli. */
  isAdmin?: boolean;
}

/* ---------- správa ---------- */

export interface AdminUserDTO {
  id: string;
  email: string;
  name: string;
  role: Role;
  isAdmin: boolean;
  blocked: boolean;
  /** Přihlásil se už přes Microsoft (má oid). Předem založený účet ho nemá. */
  microsoft: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export type AdminAction = 'user_create' | 'role' | 'admin' | 'block' | 'unblock';

export interface AdminEventDTO {
  id: string;
  ts: string;
  actor: string | null;
  target: string | null;
  action: AdminAction;
  details: Record<string, unknown>;
}

/** Pravidla brány: nástroj → role, které ho smí použít. */
export interface RuleDTO {
  tool: string;
  roles: Role[];
  why: string;
}

export const ROLES: Role[] = ['vedeni', 'pm', 'tym'];

export interface MeDTO {
  user: UserDTO;
  theme: Theme;
  ai: AiStatus;
  authMode: 'microsoft' | 'dev';
}

export type AiStatus = 'ok' | 'connecting' | 'down';

export interface ConversationDTO {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  /** Kdy uživatel chat připnul (null = nepřipnutý). */
  pinnedAt: string | null;
  /** Chat ze spuštění naplánované úlohy. */
  scheduleId?: string | null;
  /** Výsledek úlohy, který uživatel ještě neotevřel. */
  unread?: boolean;
  /** Rozběhnutý tah, pokud existuje (po znovunačtení stránky se na něj frontend znovu napojí). */
  activeTurnId?: string | null;
}

export type MessageStatus = 'streaming' | 'done' | 'error';

export interface AttachmentDTO {
  id: string;
  name: string;
  mime: string;
  size: number;
}

/** Limity příloh (stejné na frontendu i backendu). */
export const ATTACHMENT_LIMITS = { maxFiles: 5, maxBytes: 20 * 1024 * 1024, maxTotalBytes: 40 * 1024 * 1024 } as const;

export interface MessageDTO {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  status: MessageStatus;
  turnId: string | null;
  createdAt: string;
  /** Přílohy zprávy uživatele. */
  attachments?: AttachmentDTO[];
  steps?: Step[];
  sources?: SourceRef[];
  error?: string | null;
  /** Snímky datasetů, na které odkazují widgety v odpovědi (podle ID datasetu). */
  datasets?: Record<string, DatasetSnapshot>;
}

export interface ConversationDetailDTO extends ConversationDTO {
  messages: MessageDTO[];
}

export interface SendMessageResponse {
  conversation: ConversationDTO;
  userMessage: MessageDTO;
  assistantMessage: MessageDTO;
  turnId: string;
}

export interface MemoryNoteDTO {
  id: string;
  type: MemoryType;
  text: string;
  sourceConversationId: string | null;
  createdAt: string;
  /** user = zapsal na přání uživatele (pamet_zapsat), auto = Sam sám po utichnutí chatu. */
  source?: 'user' | 'auto';
}

/** Celá paměť pro nastavení: popis uživatele, poznámky a shrnutí chatů (historie). */
export interface MemoryOverviewDTO {
  autoMemory: boolean;
  profile: string | null;
  profileUpdatedAt: string | null;
  notes: MemoryNoteDTO[];
  history: { conversationId: string; title: string; summary: string; updatedAt: string }[];
}

export interface AuditEntryDTO {
  id: string;
  ts: string;
  /** Jen v přehledu správce (přístupy všech). */
  userName?: string | null;
  userEmail?: string | null;
  caller: 'ai' | 'widget' | 'user';
  tool: string;
  params: Record<string, unknown>;
  decision: 'allow' | 'deny';
  rule: string | null;
  reason: string | null;
  rows: number | null;
  ms: number | null;
  source: string | null;
  conversationId: string | null;
  turnId: string | null;
}

/* ---------- pohledy ---------- */

export interface ViewDTO {
  id: string;
  title: string;
  spec: WidgetSpec;
  query: ViewQuery;
  createdAt: string;
  /** Uživatel na nástroj pohledu nemá právo (pohled je vidět, ale neotevře se). */
  locked: boolean;
  /** Šířka karty na úvodní stránce ve sloupcích mřížky (2–6 ze 6); null = podle typu widgetu (viewCols). */
  cols: number | null;
  /** Karta je na úvodní stránce (skrytý pohled zůstává v menu). */
  onHome: boolean;
}

/** Mřížka přehledu má 6 sloupců; karta zabírá 2 (třetina) až 6 (celá šířka). */
export const VIEW_GRID = { columns: 6, minCols: 2, maxCols: 6 } as const;

/** Skutečná šířka karty ve sloupcích: uložená, jinak podle typu widgetu. */
export function viewCols(v: { cols: number | null; spec: WidgetSpec }): number {
  if (v.cols) return Math.min(VIEW_GRID.maxCols, Math.max(VIEW_GRID.minCols, v.cols));
  const s = v.spec;
  if (s.type === 'table' || s.type === 'kanban') return 6;
  if (s.type === 'stats') return s.items.length <= 2 ? 2 : 3;
  if (s.type === 'bar' || s.type === 'line' || s.type === 'area') return 3;
  return 2;
}

export interface ViewQuery {
  tool: string;
  /** Parametry nástroje. Datum může být pohyblivé: { "$rel": "week:0:start" } apod. (docs/10). */
  params: Record<string, unknown>;
}

export type ViewDataDTO =
  | { ok: true; dataset: DatasetSnapshot; params: Record<string, unknown> }
  | { ok: false; denied: boolean; error: string };

/* ---------- události SSE (backend → prohlížeč) ---------- */

export type TurnPhase = 'thinking' | 'tools' | 'writing' | 'done' | 'error';

export type TurnEvent =
  | { type: 'phase'; phase: TurnPhase }
  | { type: 'step'; step: Step }
  | { type: 'text'; delta: string; replace?: boolean }
  | { type: 'done'; message: MessageDTO }
  | { type: 'error'; message: MessageDTO; error: string };

/** Hlavička, kterou frontend posílá u měnících požadavků (ochrana proti CSRF). */
export const CSRF_HEADER = 'x-sam-request';
